import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import { 
  Shield, RefreshCw, Search, Globe, AlertTriangle, Database, 
  ExternalLink, CheckCircle, XCircle, Loader2, Eye, FileText, Copy
} from 'lucide-react';
import api from '@/lib/api';

export function ThreatIntelPage() {
  const [stats, setStats] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  
  // Lookup state
  const [lookupType, setLookupType] = useState('ip');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadIndicators();
  }, [typeFilter, sourceFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, feedsRes, indicatorsRes] = await Promise.all([
        api.get('/threat-intel/stats'),
        api.get('/threat-intel/feeds'),
        api.get('/threat-intel/indicators?limit=100')
      ]);
      setStats(statsRes.data);
      setFeeds(feedsRes.data.feeds);
      setIndicators(indicatorsRes.data.indicators);
    } catch (error) {
      console.error('Error loading threat intel:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadIndicators = async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('indicator_type', typeFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      params.append('limit', '100');
      const response = await api.get(`/threat-intel/indicators?${params}`);
      setIndicators(response.data.indicators);
    } catch (error) {
      console.error('Error loading indicators:', error);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await api.post('/threat-intel/sync');
      setTimeout(loadData, 5000);
    } catch (error) {
      console.error('Error syncing feeds:', error);
    } finally {
      setTimeout(() => setIsSyncing(false), 5000);
    }
  };

  const handleLookup = async () => {
    if (!lookupValue.trim()) return;
    setIsLookingUp(true);
    setLookupResult(null);
    try {
      let response;
      if (lookupType === 'ip') {
        response = await api.post(`/threat-intel/check/ip?ip=${encodeURIComponent(lookupValue)}`);
      } else if (lookupType === 'domain') {
        response = await api.post(`/threat-intel/check/domain?domain=${encodeURIComponent(lookupValue)}`);
      } else {
        response = await api.post(`/threat-intel/check/hash?file_hash=${encodeURIComponent(lookupValue)}`);
      }
      setLookupResult(response.data);
    } catch (error) {
      setLookupResult({ error: 'Failed to perform lookup' });
    } finally {
      setIsLookingUp(false);
    }
  };

  const getSourceColor = (source) => {
    const colors = {
      threatfox: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      urlhaus: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      malwarebazaar: 'bg-red-500/20 text-red-400 border-red-500/30',
      feodo: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      manual: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return colors[source] || 'bg-muted text-muted-foreground';
  };

  const getTypeIcon = (type) => {
    const icons = { ip: Globe, domain: Globe, url: ExternalLink, file_hash: FileText };
    const Icon = icons[type] || Shield;
    return <Icon className="h-4 w-4" />;
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  return (
    <div className="min-h-screen" data-testid="threat-intel-page">
      <Header title="Threat Intelligence" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Indicators</p>
                  <p className="text-3xl font-mono font-bold text-primary">{stats?.total_indicators || 0}</p>
                </div>
                <Database className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Last 24h</p>
                  <p className="text-3xl font-mono font-bold text-success">{stats?.recent_24h || 0}</p>
                </div>
                <RefreshCw className="h-8 w-8 text-success opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Active Feeds</p>
                  <p className="text-3xl font-mono font-bold text-info">{feeds?.length || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Critical IoCs</p>
                  <p className="text-3xl font-mono font-bold text-destructive">
                    {stats?.by_severity?.find(s => s.severity === 'critical')?.count || 0}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-destructive opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="indicators" className="space-y-4">
          <TabsList className="bg-secondary">
            <TabsTrigger value="indicators">Indicators</TabsTrigger>
            <TabsTrigger value="lookup">IoC Lookup</TabsTrigger>
            <TabsTrigger value="feeds">Feeds</TabsTrigger>
          </TabsList>
          
          {/* Indicators Tab */}
          <TabsContent value="indicators" className="space-y-4">
            <Card className="bg-card/50 border-border">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="type-filter">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="ip">IP Address</SelectItem>
                      <SelectItem value="domain">Domain</SelectItem>
                      <SelectItem value="url">URL</SelectItem>
                      <SelectItem value="file_hash">File Hash</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="source-filter">
                      <SelectValue placeholder="Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="threatfox">ThreatFox</SelectItem>
                      <SelectItem value="urlhaus">URLhaus</SelectItem>
                      <SelectItem value="malwarebazaar">MalwareBazaar</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleSync} disabled={isSyncing} data-testid="sync-feeds-btn">
                    {isSyncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Sync Feeds
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                  Threat Indicators ({indicators.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Indicator</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Classification</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Last Seen</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {indicators.map((indicator) => (
                        <TableRow 
                          key={indicator.id} 
                          className="hover:bg-secondary/50 cursor-pointer"
                          onClick={() => setSelectedIndicator(indicator)}
                          data-testid={`indicator-row-${indicator.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getTypeIcon(indicator.indicator_type)}
                              <span className="text-xs uppercase">{indicator.indicator_type}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs font-mono bg-secondary px-2 py-1 rounded max-w-[300px] block truncate">
                              {indicator.indicator_value}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge className={cn("border text-xs", getSourceColor(indicator.source))}>
                              {indicator.source}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{indicator.threat_classification || '-'}</TableCell>
                          <TableCell>
                            <Badge className={cn("border", getSeverityColor(indicator.severity))}>
                              {indicator.severity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground font-mono">
                            {formatDate(indicator.last_seen)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSelectedIndicator(indicator); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IoC Lookup Tab */}
          <TabsContent value="lookup" className="space-y-4">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Indicator of Compromise Lookup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Select value={lookupType} onValueChange={setLookupType}>
                    <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ip">IP Address</SelectItem>
                      <SelectItem value="domain">Domain</SelectItem>
                      <SelectItem value="hash">File Hash</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={lookupType === 'ip' ? '192.168.1.1' : lookupType === 'domain' ? 'example.com' : 'SHA256 hash...'}
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value)}
                    className="flex-1 font-mono"
                    data-testid="lookup-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                  />
                  <Button onClick={handleLookup} disabled={isLookingUp || !lookupValue.trim()} data-testid="lookup-btn">
                    {isLookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                
                {lookupResult && (
                  <div className="mt-6 p-4 bg-secondary/50 rounded-sm border border-border">
                    <div className="flex items-center gap-3 mb-4">
                      {lookupResult.is_malicious ? (
                        <><XCircle className="h-6 w-6 text-destructive" /><span className="font-display text-lg uppercase text-destructive">MALICIOUS</span></>
                      ) : (
                        <><CheckCircle className="h-6 w-6 text-success" /><span className="font-display text-lg uppercase text-success">CLEAN</span></>
                      )}
                    </div>
                    {lookupResult.local_match && (
                      <div className="mb-4 p-3 bg-destructive/10 rounded border border-destructive/30">
                        <h4 className="font-display text-sm uppercase text-destructive mb-2">Local Database Match</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>Source: <span className="font-mono">{lookupResult.local_match.source}</span></div>
                          <div>Classification: <span className="font-mono">{lookupResult.local_match.threat_classification}</span></div>
                          <div>Severity: <span className="font-mono">{lookupResult.local_match.severity}</span></div>
                        </div>
                      </div>
                    )}
                    {!lookupResult.local_match && !lookupResult.is_malicious && (
                      <p className="text-sm text-muted-foreground">Nenhum indicador malicioso encontrado para este valor em nossa base de dados.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feeds Tab */}
          <TabsContent value="feeds" className="space-y-4">
            <Card className="bg-card/50 border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                  Threat Intelligence Feeds
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Sync All
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {feeds.map((feed) => (
                    <div key={feed.id} className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-primary" />
                          <h4 className="font-display uppercase tracking-wider">{feed.name}</h4>
                        </div>
                        <Badge variant={feed.is_active ? 'default' : 'secondary'}>
                          {feed.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-mono">{feed.feed_type}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Last Sync</span><span className="font-mono text-xs">{feed.last_sync ? formatDate(feed.last_sync) : 'Never'}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Indicators</span><span className="font-mono">{feed.total_indicators}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Indicator Detail Modal */}
      <Dialog open={!!selectedIndicator} onOpenChange={() => setSelectedIndicator(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedIndicator && getTypeIcon(selectedIndicator.indicator_type)}
              <span className="font-display uppercase text-sm tracking-wider text-muted-foreground">
                {selectedIndicator?.indicator_type}
              </span>
              <Badge className={cn("border", getSeverityColor(selectedIndicator?.severity))}>
                {selectedIndicator?.severity?.toUpperCase()}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedIndicator && (
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 mt-2 pr-2">
                <Card className="bg-secondary/50">
                  <CardContent className="pt-4">
                    <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Indicador</h4>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono bg-background p-2 rounded break-all">
                        {selectedIndicator.indicator_value}
                      </code>
                      <Button size="icon" variant="ghost" onClick={() => copyToClipboard(selectedIndicator.indicator_value)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Fonte</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Feed</span>
                          <Badge className={cn("border text-xs", getSourceColor(selectedIndicator.source))}>
                            {selectedIndicator.source}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Tipo</span>
                          <span className="font-mono text-sm uppercase">{selectedIndicator.indicator_type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Confiança</span>
                          <span className="font-mono text-sm">{Math.round((selectedIndicator.confidence || 0) * 100)}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Ameaça</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Classificação</span>
                          <span className="font-mono text-sm">{selectedIndicator.threat_classification || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Severidade</span>
                          <Badge className={cn("border", getSeverityColor(selectedIndicator.severity))}>
                            {selectedIndicator.severity?.toUpperCase()}
                          </Badge>
                        </div>
                        {selectedIndicator.threat_names?.length > 0 && (
                          <div>
                            <span className="text-sm text-muted-foreground">Malware</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedIndicator.threat_names.map((n, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{n}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-secondary/50">
                  <CardContent className="pt-4">
                    <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Histórico</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Primeiro visto</span>
                        <span className="font-mono text-sm">{formatDate(selectedIndicator.first_seen)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Último visto</span>
                        <span className="font-mono text-sm">{formatDate(selectedIndicator.last_seen)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Detecções</span>
                        <span className="font-mono text-sm">{selectedIndicator.detection_count || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {selectedIndicator.tags?.length > 0 && (
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedIndicator.tags.map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" asChild>
                    <a href={`https://www.virustotal.com/gui/search/${encodeURIComponent(selectedIndicator.indicator_value)}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Verificar no VirusTotal
                    </a>
                  </Button>
                  <Button variant="ghost" onClick={() => copyToClipboard(selectedIndicator.indicator_value)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ThreatIntelPage;
